import { site } from "../lib/siteConfig";
import { Mail, Instagram, Globe, MessageCircle } from "lucide-react";

const QR = ({ data, size = 200, testid }) => (
  <img
    data-testid={testid}
    alt="QR Code"
    src={`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&format=png&margin=0&qzone=0&data=${encodeURIComponent(
      data
    )}`}
    className="w-full h-full object-contain"
  />
);

/* ------------------------------ FRONT ------------------------------ */
const Front = () => (
  <section
    data-testid="business-card-front"
    className="bc-page relative overflow-hidden bg-brand-ink text-white"
  >
    {/* Oversized monogram watermark (clipped to ink area only) */}
    <div
      aria-hidden
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      <div
        className="absolute font-display italic font-black leading-none select-none"
        style={{
          fontSize: "80mm",
          color: "rgba(228, 220, 207, 0.11)",
          right: "-14mm",
          bottom: "-18mm",
        }}
      >
        FG
      </div>
    </div>

    <div className="relative h-full w-full flex flex-col justify-between" style={{ padding: "6mm 7mm" }}>
      {/* Top brand */}
      <div className="flex items-start justify-between">
        <div>
          <div
            className="font-display leading-none text-white"
            style={{ fontSize: "4.2mm" }}
          >
            Gorla<span className="italic font-normal"> Properties</span>
          </div>
          <div
            className="mt-[1.2mm] uppercase font-semibold text-brand-beige whitespace-nowrap"
            style={{ fontSize: "1.7mm", letterSpacing: "0.24em" }}
          >
            Rendita immobiliare
          </div>
        </div>
      </div>

      {/* Name block */}
      <div>
        <h1
          className="font-display font-black text-white leading-[0.95]"
          style={{ fontSize: "9.5mm" }}
        >
          Francesca
          <br />
          <span className="italic font-normal">Gorla</span>
        </h1>
        <div
          className="mt-[2.5mm] uppercase font-semibold text-brand-beige"
          style={{ fontSize: "1.8mm", letterSpacing: "0.28em" }}
        >
          <span className="inline-block align-middle h-px bg-brand-beige/70" style={{ width: "5mm", marginRight: "2mm" }} />
          Consulente Immobiliare Abilitata
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------ BACK ------------------------------ */
const Back = () => (
  <section
    data-testid="business-card-back"
    className="bc-page relative overflow-hidden bg-brand-bg text-brand-ink"
  >
    {/* Top strip */}
    <div
      aria-hidden
      className="absolute top-0 left-0 right-0 bg-brand-ink"
      style={{ height: "5mm" }}
    />
    <div
      aria-hidden
      className="absolute left-0 right-0 bg-brand-beige"
      style={{ top: "5mm", height: "0.7mm" }}
    />

    <div
      className="relative h-full w-full flex items-stretch gap-[5mm]"
      style={{ padding: "8mm 7mm 6mm 7mm" }}
    >
      {/* Left: contact info */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div
            className="font-display font-bold text-brand-ink leading-tight"
            style={{ fontSize: "3.8mm" }}
          >
            Parliamo del <br/><span className="italic font-normal">tuo immobile.</span>
          </div>
        </div>

        <div className="space-y-[2mm] min-w-0" style={{ fontSize: "2.1mm" }}>
          <div className="flex items-center gap-[2mm]">
            <div
              className="flex items-center justify-center rounded-full bg-brand-ink text-white shrink-0"
              style={{ width: "4.5mm", height: "4.5mm" }}
            >
              <MessageCircle size={7} strokeWidth={2.2} />
            </div>
            <span className="font-semibold text-brand-ink">
              {site.whatsappNumber}
            </span>
          </div>

          <div className="flex items-center gap-[2mm] min-w-0">
            <div
              className="flex items-center justify-center rounded-full bg-brand-ink text-white shrink-0"
              style={{ width: "4.5mm", height: "4.5mm" }}
            >
              <Mail size={7} strokeWidth={2.2} />
            </div>
            <span className="text-brand-ink truncate">{site.email}</span>
          </div>

          <div className="flex items-center gap-[2mm]">
            <div
              className="flex items-center justify-center rounded-full bg-brand-ink text-white shrink-0"
              style={{ width: "4.5mm", height: "4.5mm" }}
            >
              <Instagram size={7} strokeWidth={2.2} />
            </div>
            <span className="text-brand-ink">{site.instagramHandle}</span>
          </div>

          <div className="flex items-center gap-[2mm]">
            <div
              className="flex items-center justify-center rounded-full bg-brand-ink text-white shrink-0"
              style={{ width: "4.5mm", height: "4.5mm" }}
            >
              <Globe size={7} strokeWidth={2.2} />
            </div>
            <span className="text-brand-ink font-semibold">gorlaproperties.com</span>
          </div>
        </div>

        <div
          className="uppercase text-brand-muted flex items-center justify-between gap-2"
          style={{ fontSize: "1.4mm", letterSpacing: "0.18em" }}
        >
          <span>{site.rea}</span>
          <span>{site.vat}</span>
        </div>
      </div>

      {/* Divider */}
      <div
        aria-hidden
        className="bg-brand-line self-stretch"
        style={{ width: "0.3mm" }}
      />

      {/* Right: QR + name */}
      <div className="shrink-0 flex flex-col items-center justify-center gap-[2.5mm]" style={{ width: "23mm" }}>
        <div className="text-center">
          <div
            className="font-display font-bold text-brand-ink leading-none whitespace-nowrap"
            style={{ fontSize: "2.9mm" }}
          >
            Francesca <span className="italic font-normal">Gorla</span>
          </div>
          <div
            className="mt-[1mm] uppercase text-brand-muted whitespace-nowrap"
            style={{ fontSize: "1.35mm", letterSpacing: "0.18em" }}
          >
            Consulente Immobiliare
          </div>
        </div>

        <div className="flex flex-col items-center gap-[1mm]">
          <div
            className="bg-white border border-brand-line"
            style={{ width: "22mm", height: "22mm", padding: "0.8mm" }}
          >
            <QR data={site.whatsappUrl} size={240} testid="qr-whatsapp-bc" />
          </div>
          <div
            className="uppercase font-semibold text-brand-ink flex items-center gap-[1mm] whitespace-nowrap"
            style={{ fontSize: "1.5mm", letterSpacing: "0.2em" }}
          >
            <MessageCircle size={6} className="text-[#25D366]" strokeWidth={2.4} />
            Scrivimi
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------ WRAPPER ------------------------------ */
export const BusinessCard = () => {
  return (
    <div data-testid="business-card-container" className="bc-viewport">
      <style>{`
        @page { size: 85mm 55mm; margin: 0; }
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .bc-controls { display: none !important; }
          .bc-viewport {
            background: #fff !important;
            padding: 0 !important;
            gap: 0 !important;
            min-height: 0 !important;
          }
          .bc-page {
            box-shadow: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
            page-break-after: always;
            break-after: page;
          }
          .bc-page:last-of-type {
            page-break-after: auto;
            break-after: auto;
          }
          .bc-page, .bc-page * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
        .bc-page, .bc-page * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          color-adjust: exact;
        }
        .bc-viewport {
          min-height: 100vh;
          background: #f0eee8;
          padding: 40px 16px 48px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }
        .bc-page {
          width: 85mm;
          height: 55mm;
          box-shadow: 0 24px 48px rgba(11, 26, 44, 0.14), 0 6px 14px rgba(11, 26, 44, 0.08);
          border-radius: 2mm;
          position: relative;
        }
      `}</style>

      <div className="bc-controls max-w-[85mm] w-full flex flex-col items-stretch gap-3">
        <div className="flex items-center justify-between gap-3">
          <a
            href="/"
            data-testid="bc-back"
            className="text-sm text-brand-muted hover:text-brand-ink transition-colors"
          >
            ← Torna al sito
          </a>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="bc-print"
            className="inline-flex items-center gap-2 bg-brand-ink text-white text-sm font-medium px-5 py-3 rounded-sm hover:bg-brand-inkHover transition-colors"
          >
            Stampa · Salva come PDF
          </button>
        </div>
        <p className="text-xs text-brand-muted text-center">
          Biglietto da visita · 85 × 55 mm · Fronte + Retro
        </p>
      </div>

      <Front />
      <Back />

      <p className="bc-controls text-xs text-brand-muted text-center max-w-[85mm] leading-relaxed">
        Suggerimento: Chrome o Safari → Stampa → Salva come PDF · Formato personalizzato 85×55&nbsp;mm · Senza margini · Attiva “Grafica di sfondo”.
      </p>
    </div>
  );
};

export default BusinessCard;
